/** 突合の実行履歴。1行=1回。開くとその回のレポート全文が見える。 */
import { Alert, Card, Descriptions, Drawer, Empty, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { Link } from "react-router-dom";

import type { ReconcileRun, ReconcileSaasRow } from "../../lib/api";
import { jst } from "../../lib/format";
import { errorText, useIdpPerms, useReconcileRun, useReconcileRuns } from "./hooks";

const { Text } = Typography;

/** 起動のしかた(app/idp/constants.py の TRIGGERS)。 */
const TRIGGER_LABEL: Record<string, string> = {
  schedule: "定期実行",
  manual: "手動",
  cli: "コマンド",
};

/** レポートのメタ情報。指摘の一覧には出さない。 */
const META = ["mode", "status", "connector", "item_id"];

function triggerTag(v: string) {
  const color = v === "schedule" ? "blue" : v === "cli" ? "default" : "geekblue";
  return <Tag color={color}>{TRIGGER_LABEL[v] ?? v}</Tag>;
}

function ReportBlock({ name, row }: { name: string; row: ReconcileSaasRow }) {
  const cats = Object.entries(row).filter(([k]) => !META.includes(k));
  return (
    <Card size="small" title={name} style={{ marginBottom: 8 }}>
      <Descriptions size="small" column={1}>
        <Descriptions.Item label="コネクタ">{row.connector || "—"}</Descriptions.Item>
        <Descriptions.Item label="扱い">{row.mode}</Descriptions.Item>
        {cats.map(([k, v]) => (
          <Descriptions.Item label={k} key={k}>
            {Array.isArray(v) ? (v.length ? v.join(", ") : "なし") : String(v)}
          </Descriptions.Item>
        ))}
      </Descriptions>
    </Card>
  );
}

function RunDrawer({ id, onClose }: { id: number | null; onClose: () => void }) {
  const query = useReconcileRun(id);
  const d = query.data;
  const saas = Object.entries(d?.report?.saas ?? {});

  return (
    <Drawer
      open={!!id}
      width={720}
      onClose={onClose}
      destroyOnClose
      title={d ? `突合 #${d.id}(${jst(d.started_at)})` : "突合"}
    >
      {query.isError && (
        <Alert
          type="error"
          showIcon
          message="この回を取得できなかった"
          description={errorText(query.error)}
        />
      )}
      {d && (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="起動">{triggerTag(d.trigger)}</Descriptions.Item>
            <Descriptions.Item label="実行者">{d.actor || "—"}</Descriptions.Item>
            <Descriptions.Item label="開始">{jst(d.started_at)}</Descriptions.Item>
            <Descriptions.Item label="終了">{jst(d.finished_at)}</Descriptions.Item>
            <Descriptions.Item label="指摘">{d.findings} 件</Descriptions.Item>
            <Descriptions.Item label="前回">
              {d.first ? "初回(差分なし)" : d.previous_id ? `#${d.previous_id} と比較` : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="新規">
              {d.new.length ? d.new.map((k) => <Tag color="red" key={k}>{k}</Tag>) : "なし"}
            </Descriptions.Item>
            <Descriptions.Item label="解消">
              {d.resolved.length
                ? d.resolved.map((k) => <Tag color="green" key={k}>{k}</Tag>)
                : "なし"}
            </Descriptions.Item>
          </Descriptions>

          <div>
            <Typography.Title level={5}>レポート</Typography.Title>
            {saas.length ? (
              saas.map(([name, row]) => <ReportBlock key={name} name={name} row={row} />)
            ) : (
              <Empty description="レポートが空(有効なカタログ項目が無かった)" />
            )}
          </div>
        </Space>
      )}
    </Drawer>
  );
}

export function RunsPage() {
  const { canView } = useIdpPerms();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [openId, setOpenId] = useState<number | null>(null);
  const query = useReconcileRuns({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    enabled: canView,
  });

  const columns: ColumnsType<ReconcileRun> = [
    {
      title: "日時",
      width: 190,
      render: (_: unknown, row) => (
        <a onClick={() => setOpenId(row.id)}>{jst(row.started_at) || `#${row.id}`}</a>
      ),
    },
    { title: "起動", width: 110, render: (_: unknown, row) => triggerTag(row.trigger) },
    { title: "指摘数", width: 90, align: "right", dataIndex: "findings" },
    {
      title: "新規",
      width: 90,
      align: "right",
      render: (_: unknown, row) =>
        row.new_count ? <Text type="danger">{row.new_count}</Text> : 0,
    },
    {
      title: "解消",
      width: 90,
      align: "right",
      render: (_: unknown, row) =>
        row.resolved_count ? <Text type="success">{row.resolved_count}</Text> : 0,
    },
    { title: "実行者", width: 160, render: (_: unknown, row) => row.actor || "—" },
  ];

  return (
    <div>
      <Typography.Title level={3}>実行履歴</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        <Link to="/idp/reconciliation">アカウント突合</Link>
        の実行を新しい順に並べる。定期実行は「SaaSコネクタ設定」の間隔で動く。
      </Typography.Paragraph>

      {!canView && <Alert type="info" showIcon message="履歴の閲覧には idp:viewer が要る" />}

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="履歴を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Table<ReconcileRun>
        scroll={{ x: "max-content" }}
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={query.data?.items ?? []}
        loading={query.isFetching}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [20, 50, 100],
          showTotal: (total) => `全 ${total} 回`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        locale={{ emptyText: "まだ突合していない" }}
      />

      <RunDrawer id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
